import { asTrackingPayload, makeWidgetProps, asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, useState } from 'react';
import { connect } from 'react-redux';
import { ID_LAYER_HEADER__CA__INSURANCES } from 'Components/utils/identifiers/constants';
import { LAYER_NAME_UPSELL_INSURANCES } from 'Components/utils/layers/layers.constants';
import { useAppContext } from 'Context/ReactAppContext';
import type { updateCartParams } from 'Components/widgets/cart/cart.actions';
import { updateBookingCart } from 'Components/widgets/cart/cart.actions';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { ClosableLayer } from 'Components/widgets/layer/ClosableLayer';
import { StayLayerHeader } from 'Components/widgets/stay-layer-header/StayLayerHeader';
import { NInsurance } from './NInsurance';
const prepareInsuranceScheduleParameter = ({ pricingInfo: { schedules = [] } = {} } = {}) =>
  schedules.map((schedule) => ({
    attendees: schedule.attendees,
    start_date: schedule.startDate,
    end_date: schedule.endDate,
  }));
type Props = PropsWithPageTracking<{
  forwardRef: ReactRef;
  isCartLocked?: boolean;
  selectedInsuranceId: string;
  stay: StayEntity;
  updateCart: (arg0: updateCartParams) => Promise<unknown>;
}>;
export const InsuranceLayer = ({
  forwardRef,
  isCartLocked = false,
  selectedInsuranceId,
  stay = makeWidgetProps<StayEntity>({}),
  updateCart,
  tracking,
}: Props) => {
  const appContext = useAppContext();
  const [showInsuranceLayer, setShowInsuranceLayer] = useState(false);
  const { trackClickEvent } = useTrackEvent();
  const closeInsuranceLayer = () => {
    setShowInsuranceLayer(false);
  };
  const removeInsurance = () => {
    trackClickEvent(
      TRACK_EVENT_NAMES.CLICK_UPSELL,
      asTrackingPayload({
        detail_click: 'remove_insurance',
      }),
    );
    return updateCart({
      action: 'remove',
      bookingId: stay.booking.id,
      context: appContext,
      id: selectedInsuranceId,
      serviceName: 'INSURANCE',
      stayIndex: stay.stayIndex,
    });
  };
  const addInsurance = (ins) => {
    trackClickEvent(
      TRACK_EVENT_NAMES.CLICK_UPSELL,
      asTrackingPayload({
        detail_click: 'add_insurance',
      }),
    );
    return updateCart({
      action: 'update',
      bookingId: stay.booking.id,
      context: appContext,
      id: ins.pricingInfo.id,
      schedules: prepareInsuranceScheduleParameter(ins),
      serviceName: 'INSURANCE',
      stayIndex: stay.stayIndex,
    });
  };
  const title = t('stays.linkLabels.upsells.addInsuranceLabel');
  return (
    <ClosableLayer
      {...asWidgetProps<ComponentProps<typeof ClosableLayer>>({
        ariaLabel: title,
        ariaLabelledby: ID_LAYER_HEADER__CA__INSURANCES,
        externalFocusTrapRefs: [forwardRef],
        hasAnimateEnter: true,
        header: (
          <StayLayerHeader
            {...asWidgetProps<ComponentProps<typeof StayLayerHeader>>({
              id: ID_LAYER_HEADER__CA__INSURANCES,
              stay: stay,
              title: title,
            })}
          />
        ),
        name: LAYER_NAME_UPSELL_INSURANCES,
        onBack: showInsuranceLayer ? closeInsuranceLayer : undefined,
        params: ['id', 'stayId'],
        tracking: tracking,
      })}
    >
      <div className="pb-120">
        <NInsurance
          {...asWidgetProps<ComponentProps<typeof NInsurance>>({
            addedInsurance: selectedInsuranceId,
            addInsurance: addInsurance,
            insurance: stay?.upsells?.insurance?.[0],
            isCartLocked: isCartLocked,
            removeInsurance: removeInsurance,
          })}
        />
      </div>
    </ClosableLayer>
  );
};
const mapStateToProps = (state, { stay }) => {
  const cartBookings = state.memberSpace.cart?.data?.bookingsCart?.cartBookings;
  const cartBooking = cartBookings?.find(({ bookingId }) => bookingId === stay.booking.id);
  return {
    isCartLocked: !cartBookings || !!cartBooking?.hasServicesInOption,
    selectedInsuranceId:
      (cartBooking?.stayServices || []).find(({ stayIndex }) => stayIndex === stay.stayIndex)
        ?.insurance?.[0]?.id || null,
  };
};
const mapDispatchToProps = {
  updateCart: updateBookingCart,
};
export const InsuranceLayerConnected = connect(mapStateToProps, mapDispatchToProps)(InsuranceLayer);
